import Arrow from "../../assets/img/arrow-left-grey-round.svg";
import useTranslator from "../../hooks/useTranslator";
import DownloadIcon from "../../assets/img/downloadIcon.svg";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  calculateOverlayScore,
  getUserDisplayName,
  getCustomIncomeDetails,
  getFieldForPolicies,
  getRentPercentage,
  haveValue,
  normalizeResolutions,
  tenantHasMissingDniNie,
  toast,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import TenantContext from "../../context/tenant/TenantContext";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/en-gb";
import AuthContext from "../../context/auth/AuthContext";
import Loader from "../../components/Loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import axiosInstance from "../../services/authWrapper";
import { transformAwardSnapshot } from "../../Utils/transformAwardSnapshot";
import { getMissingDocsFromSnapshot } from "../../Utils/getMissingDocsFromSnapshot";

const ReportOverview = (props = {}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const {
    getPropertyDetails,
    getTenantExtraData,
    deaWorkspace,
    markProfileResolved,
    getProfileResolutionStatus,
    unmarkProfileResolved,
  } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { getZipOfMultipleTenantDocs } = useContext(TenantContext);
  const {
    property_id: propertyIdProp,
    sharedprofileid: sharedprofileidProp,
    rent: rentProp,
    redirectUrl: redirectUrlProp,
    selection: selectionRawProp,
    awardSnapshotCompleted: awardSnapshotCompletedProp,
    historyEventId: historyEventIdProp,
    onResolutionChange: onResolutionChangeProp,
  } = props;

  const queryParameters =
    props.queryParameters ?? new URLSearchParams(window.location.search);
  const property_id = propertyIdProp ?? queryParameters.get("property");
  const sharedprofileid =
    sharedprofileidProp ?? queryParameters.getAll("sharedprofileid");
  const sharedProfileIdsKey = sharedprofileid.join(",");
  const rentVal = rentProp ?? queryParameters.get("rent");
  const redirectUrl = redirectUrlProp ?? queryParameters.get("redirectUrl");
  const selectionRaw = selectionRawProp ?? queryParameters.get("selection");

  const selectionSelectedIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return [entry.tenantId].filter(Boolean);
        return (entry.participants || [])
          .map((p) => p.tenantId)
          .filter(Boolean);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const awardSnapshotCompleted =
    awardSnapshotCompletedProp ??
    queryParameters.get("awardSnapshotCompleted") === "true";
  const historyEventId =
    historyEventIdProp ?? queryParameters.get("historyEventId");

  const selectionSelectedGuarantorIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return entry.guarantorIds || [];
        return (entry.participants || []).flatMap((p) => p.guarantorIds || []);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const todaysDate = (
    createdAt = null,
    locale = localStorage.getItem("i18nextLng")
  ) => {
    moment.locale(locale);
    return moment(createdAt || undefined).format("D MMMM YYYY");
  };
  const STATUS_STYLES = {
    approved: {
      backgroundColor: "#F2FBE9",
      color: "#82CF3F",
      border: ".0694vw solid #DAECC8",
      text: _t("approved"),
      img: "/images/tick-new.svg",
      insurable: _t("insurable"),
    },
    review: {
      backgroundColor: "#FFFBEC",
      color: "#E1BB20",
      border: ".0694vw solid #F2E09B",
      text: _t("manualReview"),
      img: "/images/warning.svg",
      insurable: _t("insurable"),
    },
    rejected: {
      backgroundColor: "#FFE2E2",
      color: "#FF3C3C",
      border: ".0694vw solid #EB9B9C",
      text: _t("declined"),
      img: "/images/notApproved.svg",
      insurable: _t("notInsurable"),
    },
  };

  const [status, setStatus] = useState("approved");
  const [addressVal, setAddressVal] = useState("");
  const [tenantData, setTenantData] = useState([]);
  const [showLocalLoading, setShowLocalLoading] = useState(true);
  const [individualError, setIndividualError] = useState(false);
  const [individualWarnings, setIndividualWarnings] = useState(false);
  const [lowScoreCheck, setLowScoreCheck] = useState(false);
  const [higherScoreProfile, setHigherScoreProfile] = useState(false);
  const [missingDniNieWarning, setMissingDniNieWarning] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedResolveTenantIds, setSelectedResolveTenantIds] = useState([]);
  const [resolvingDocs, setResolvingDocs] = useState(false);
  const [showResolveNoteModal, setShowResolveNoteModal] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [individualResolutions, setIndividualResolutions] = useState({});

  useEffect(() => {
    const loadData = async () => {
      setShowLocalLoading(true);

      let propertyData = null;
      if (haveValue(property_id)) {
        try {
          const res = await getPropertyDetails(property_id);
          propertyData = res.properties;
          setAddressVal(propertyData);
        } catch (err) {
          console.log(err);
        }
      }

      if (
        !haveValue(sharedprofileid) &&
        !awardSnapshotCompleted &&
        !historyEventId
      ) {
        setShowLocalLoading(false);
        return;
      }

      let tenantDataLoaded = false;
      try {
        let rawTenants = [];

        if (
          (awardSnapshotCompleted || historyEventId) &&
          haveValue(property_id)
        ) {
          try {
            const snapshotRes = historyEventId
              ? await axiosInstance.get(
                  `${process.env.REACT_APP_SERVER_URL}/users/history-award-snapshot/${historyEventId}`
                )
              : await axiosInstance.post(
                  `${process.env.REACT_APP_SERVER_URL}/users/award-snapshot`,
                  { propertyId: property_id }
                );
            const snapshotData = snapshotRes.data?.data;
            const snapshotProfiles = transformAwardSnapshot(snapshotData);

            const snapshotCreatedAt =
              propertyData?.award_snapshot_info?.created_at;

            snapshotProfiles.forEach((profile) => {
              const tenantData = profile.tenantid;
              if (tenantData) {
                tenantData._snapshotMissingDocs = getMissingDocsFromSnapshot(
                  tenantData,
                  deaWorkspace,
                  snapshotCreatedAt
                );
                if (tenantData.gurantor_id) {
                  tenantData.gurantor_id.forEach((g) => {
                    if (g && typeof g === "object") {
                      g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                        g,
                        deaWorkspace,
                        snapshotCreatedAt
                      );
                    }
                  });
                }
              }
              if (profile.application_id?.participants) {
                profile.application_id.participants.forEach((p) => {
                  const pt = p.tenant_info;
                  if (pt) {
                    pt._snapshotMissingDocs = getMissingDocsFromSnapshot(
                      pt,
                      deaWorkspace,
                      snapshotCreatedAt
                    );
                    if (pt.gurantor_id) {
                      pt.gurantor_id.forEach((g) => {
                        if (g && typeof g === "object") {
                          g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                            g,
                            deaWorkspace,
                            snapshotCreatedAt
                          );
                        }
                      });
                    }
                  }
                });
              }
            });

            snapshotProfiles.forEach((profile) => {
              if (profile.tenantid) {
                rawTenants.push(profile.tenantid);
              }
              if (profile.application_id?.participants) {
                profile.application_id.participants.forEach((p) => {
                  if (p.tenant_info) {
                    rawTenants.push(p.tenant_info);
                  }
                });
              }
            });

            if (selectionSelectedIds) {
              rawTenants = rawTenants.filter((t) =>
                selectionSelectedIds.includes(t?.id ?? t?.tenant_id ?? t?._id)
              );
            }
          } catch (snapshotError) {
            console.error(
              "[ReportOverview] snapshot fetch failed, falling back to live data",
              snapshotError
            );
          }
        }

        if (rawTenants.length === 0 && haveValue(sharedprofileid)) {
          const tenantRes = await getTenantExtraData(sharedprofileid);
          rawTenants = tenantRes?.success_res ?? [];
          if (selectionSelectedIds) {
            rawTenants = rawTenants.filter((t) =>
              selectionSelectedIds.includes(t?._id)
            );
          }
        }

        if (rawTenants.length > 0) {
          await handleTenantResponse(rawTenants);
          tenantDataLoaded = true;
        }
      } catch (err) {
        console.log(err);
      } finally {
        if (!tenantDataLoaded) setShowLocalLoading(false);
      }
    };

    loadData();
  }, [
    property_id,
    sharedProfileIdsKey,
    awardSnapshotCompleted,
    historyEventId,
    selectionSelectedIds,
    deaWorkspace,
    dataRefreshKey,
  ]);

  useEffect(() => {
    if (tenantData.length === 0) return;

    const result = tenantData.reduce(
      (acc, item) => {
        if (item?.errorEntries?.length > 0) {
          acc.isHardFail = true;
          acc.errorsByLabel[item.label] = item.errorEntries;
        }

        if (item?.vals?.DOCUMENTATION > 0) {
          acc.warningsById[item._id] = {
            label: item.label,
            docsMissing: item?.vals?.MISSING_DOCS,
            isResolved: !!item?.isResolved,
          };
        }

        const hasKycHardFail = (item?.errorEntries || []).some(
          ([, value]) => value?.label === "KYCHardFail"
        );
        if (item?.hasMissingDniNie && !hasKycHardFail) {
          acc.missingDniNieByLabel[item.label] = true;
        }
        return acc;
      },
      {
        isHardFail: false,
        errorsByLabel: {},
        warningsById: {},
        missingDniNieByLabel: {},
      }
    );
    setMissingDniNieWarning(result?.missingDniNieByLabel);
    console.log(result, "checkResult");
    setIndividualError(result?.errorsByLabel);
    setIndividualWarnings(result?.warningsById);
    const highPointProfile = tenantData.reduce((max, current) => {
      if (!max) return current;

      return current?.DEA_SCORE?.finalScore > max?.DEA_SCORE?.finalScore
        ? current
        : max;
    }, null);
    const profilesHaveMissingDocs = tenantData.some(
      (item) => item?.vals?.DOCUMENTATION > 0 && !item?.isResolved
    );

    setLowScoreCheck(highPointProfile?.DEA_SCORE?.finalScore <= 4);

    if (result?.isHardFail) {
      setStatus("rejected");
    } else if (
      highPointProfile?.DEA_SCORE?.finalScore > 4 &&
      !profilesHaveMissingDocs
    ) {
      setStatus("approved");
    } else {
      setStatus("review");
    }

    setHigherScoreProfile(highPointProfile);
    setShowLocalLoading(false);
  }, [tenantData]);

  const toggleResolveTenant = (tenantId) => {
    if (!tenantId) return;
    setSelectedResolveTenantIds((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  // selectedResolveTenantIds = current checked ids.
  // Resolved are checked by default; unchecking them queues an unresolve.
  const pendingResolveIds = useMemo(
    () =>
      selectedResolveTenantIds.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      ),
    [selectedResolveTenantIds, individualWarnings]
  );
  const pendingUnresolveIds = useMemo(() => {
    const selectedSet = new Set(selectedResolveTenantIds);
    return Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id)
      .filter((id) => !selectedSet.has(id));
  }, [selectedResolveTenantIds, individualWarnings]);

  const hasPendingChanges =
    pendingResolveIds.length > 0 || pendingUnresolveIds.length > 0;

  // Seed checked state with resolved ids on load/change
  useEffect(() => {
    const resolvedIds = Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id);
    setSelectedResolveTenantIds((prev) => {
      const prevPendingResolve = prev.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      );
      // keep user's pending resolves, always reflect current resolved as checked
      return [...new Set([...resolvedIds, ...prevPendingResolve])];
    });
  }, [individualWarnings]);
  const resolveActionLabel = useMemo(() => {
    const hasResolve = pendingResolveIds.length > 0;
    const hasUnresolve = pendingUnresolveIds.length > 0;
    if (hasResolve && hasUnresolve) return _t("update", "Update");
    if (hasUnresolve) return _t("unresolve", "Unresolve");
    return _t("markResolved", "Mark resolved");
  }, [pendingResolveIds, pendingUnresolveIds, _t]);

  const handleApplyResolutions = async (note) => {
    if (!hasPendingChanges) return;
    setResolvingDocs(true);
    try {
      const ops = [];
      pendingResolveIds.forEach((tenantId) =>
        ops.push(
          markProfileResolved({ tenant_id: tenantId, type: "documents", note })
        )
      );
      pendingUnresolveIds.forEach((tenantId) =>
        ops.push(unmarkProfileResolved(tenantId, "documents"))
      );
      await Promise.all(ops);
      if (pendingResolveIds.length && pendingUnresolveIds.length)
        toast.success(_t("documentsUpdated", "Documents updated"));
      else if (pendingUnresolveIds.length)
        toast.success(_t("documentsUnresolved", "Documents unresolved"));
      else
        toast.success(
          _t("documentsMarkedResolved", "Documents marked resolved")
        );
      setResolveNote("");
      setShowResolveNoteModal(false);
      setDataRefreshKey((currentKey) => currentKey + 1);
      onResolutionChangeProp?.({
        resolvedIds: pendingResolveIds,
        unresolvedIds: pendingUnresolveIds,
      });
    } catch (err) {
      console.log(err, "Error applying document resolutions");
      toast.error(_t("failedToUpdate", "Failed to update"));
    } finally {
      setResolvingDocs(false);
    }
  };

  // keep old name as alias for backwards compat if referenced elsewhere in file
  const handleMarkResolved = handleApplyResolutions;

  const handleGoBack = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate(-1);
    }
  };

  const downloadPdfAndDocuments = async () => {
    const container = document.getElementById("divToPrint");

    try {
      setShowSpinner(true);

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;

      // hide buttons
      const hiddenElements = document.querySelectorAll(".no-print");
      hiddenElements.forEach((el) => (el.style.display = "none"));

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: "#ffffff",
        scale: 2,
        ignoreElements: (element) => element.classList.contains("no-print"),
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const scale = Math.min(A4_WIDTH / imgWidth, A4_HEIGHT / imgHeight);

      const renderWidth = imgWidth * scale;
      const renderHeight = imgHeight * scale;

      const x = (A4_WIDTH - renderWidth) / 2;
      const y = (A4_HEIGHT - renderHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);

      // convert PDF to blob instead of downloading
      const pdfBlob = pdf.output("blob");

      hiddenElements.forEach((el) => (el.style.display = ""));

      // -----------------------------
      // GET BACKEND ZIP
      // -----------------------------

      const allTenants = tenantData?.map((tenant) => tenant?._id);

      const res = await getZipOfMultipleTenantDocs(allTenants);

      const backendZipBlob = res.data;

      // -----------------------------
      // UNZIP BACKEND ZIP
      // -----------------------------

      const backendZip = await JSZip.loadAsync(backendZipBlob);

      // create final zip
      const finalZip = new JSZip();

      // add generated pdf
      finalZip.file("report_and_documents.pdf", pdfBlob);

      // add backend files
      const promises = [];

      backendZip.forEach((relativePath, file) => {
        if (!file.dir) {
          promises.push(
            file.async("blob").then((content) => {
              finalZip.file(relativePath, content);
            })
          );
        }
      });

      await Promise.all(promises);

      // -----------------------------
      // GENERATE FINAL ZIP
      // -----------------------------

      const finalZipBlob = await finalZip.generateAsync({
        type: "blob",
      });

      const url = window.URL.createObjectURL(finalZipBlob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "documents.zip";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      setShowSpinner(false);
    } catch (err) {
      console.error("Error capturing element:", err);
      setShowSpinner(false);
    }
  };

  const getProfileIsResolved = async (profileId) => {
    if (!haveValue(profileId)) return null;
    try {
      const res = await getProfileResolutionStatus(profileId, "documents");
      return res?.isResolved ? res : null;
    } catch (err) {
      console.log("Failed to fetch profile resolution status", err);
      return null;
    }
  };

  const handleTenantResponse = async (response) => {
    let userIncomeObj = {};
    let tenantIncomeSum = 0;
    let guarantorIncomeSum = 0;

    // ---- Calculate Tenant + Guarantor Income ----
    for (let tenant of response) {
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const incomeSelection = tenant?.income_selection;

      // Tenant income
      const tenantIncome = getCustomIncomeDetails(
        tenant,
        user,
        _t,
        incomeSelection?.tenant_source,
        undefined,
        undefined,
        deaWorkspace,
        tenant?.custom_income_sources
      );

      const tenantAmount = tenantIncome?.amount ?? 0;
      userIncomeObj[tenant?._id] = tenantAmount;
      tenantIncomeSum += tenantAmount;

      // Guarantor incomes
      let guarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        guarantors = guarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }
      const guarantorIncomeSelection =
        incomeSelection?.[tenant?._id]?.guarantor_sources ??
        incomeSelection?.guarantor_sources;

      for (let g of guarantors) {
        const guarantorIncome = getCustomIncomeDetails(
          g,
          user,
          _t,
          guarantorIncomeSelection?.[g?._id],
          undefined,
          undefined,
          deaWorkspace,
          tenant?.custom_income_sources
        );

        userIncomeObj[g?._id] = guarantorIncome?.amount ?? 0;
        guarantorIncomeSum += guarantorIncome?.amount ?? 0;
      }
    }

    // Total income from tenant + guarantors
    const totalIncome = tenantIncomeSum + guarantorIncomeSum;

    const finalEffortRate =
      getRentPercentage(totalIncome, rentVal)?.roundValue ?? "0";

    const result = [];
    const resolutionsById = {};

    for (let idx = 0; idx < response.length; idx++) {
      const tenant = response[idx];
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const tenantSharedProfileId = sharedprofileid[idx];

      // ----- TENANT PROCESSING -----
      const tenantResolution = await getProfileIsResolved(tenant?._id);
      if (tenantResolution) resolutionsById[tenant?._id] = tenantResolution;
      const tenantIsResolved = !!tenantResolution;
      const temp = await getFieldForPolicies(
        tenant,
        user,
        finalEffortRate,
        false,
        deaWorkspace
      );

      const DEA_SCORE = calculateOverlayScore(
        temp.EFFORT_RATE_SCORE,
        temp.TENURE,
        temp.EMPLOYMENT_PREDICTABILITY,
        temp.SECTOR_CYCLICALITY,
        temp.EMPLOYER_STABILITY
      );

      const errorEntries = Object.entries(temp).filter(
        ([key, value]) => value?.isHardFail === true
      );

      let guarantorOutsideSpain = false;
      let checkGuarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        checkGuarantors = checkGuarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }
      for (let g of checkGuarantors) {
        const forensicData = g?.tenant_extra_field_id?.forensic_financial_data;
        if (forensicData) {
          for (let k in forensicData) {
            if (
              forensicData[k]?.origin &&
              forensicData[k]?.origin !== "Local"
            ) {
              guarantorOutsideSpain = true;
              break;
            }
          }
        }
        if (guarantorOutsideSpain) break;
      }
      if (guarantorOutsideSpain) {
        errorEntries.push([
          "GUARANTOR_OUTSIDE_SPAIN",
          { isHardFail: true, label: "guarantorLivingOutsideSpain" },
        ]);
      }

      const label = getUserDisplayName(tenant);

      tenant.label = label;

      result.push({
        ...tenant,
        role: "tenant",
        sharedProfileId: tenantSharedProfileId,
        DEA_SCORE,
        errorEntries,
        vals: temp,
        income: userIncomeObj?.[tenant?._id],
        hasMissingDniNie: tenantHasMissingDniNie(tenant),
        isResolved: tenantIsResolved,
      });

      // ----- GUARANTOR PROCESSING (same as tenant) -----
      let guarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        guarantors = guarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }

      for (let g of guarantors) {
        const gResolution = await getProfileIsResolved(g?._id);
        if (gResolution) resolutionsById[g?._id] = gResolution;
        const gIsResolved = !!gResolution;
        const gTemp = await getFieldForPolicies(
          g,
          user,
          finalEffortRate,
          false,
          deaWorkspace
        );

        const gDEA_SCORE = calculateOverlayScore(
          gTemp.EFFORT_RATE_SCORE,
          gTemp.TENURE,
          gTemp.EMPLOYMENT_PREDICTABILITY,
          gTemp.SECTOR_CYCLICALITY,
          gTemp.EMPLOYER_STABILITY
        );

        const gErrorEntries = Object.entries(gTemp).filter(
          ([key, value]) => value?.isHardFail === true
        );

        const gLabel = getUserDisplayName(g);

        g.label = gLabel;

        result.push({
          ...g,
          role: "guarantor",
          sharedProfileId: tenantSharedProfileId,
          parentTenantId: tenant?._id,
          DEA_SCORE: gDEA_SCORE,
          errorEntries: gErrorEntries,
          vals: gTemp,
          income: userIncomeObj?.[g?._id],
          hasMissingDniNie: tenantHasMissingDniNie(g),
          isResolved: gIsResolved,
        });
      }
    }

    setTenantData(result);
    setIndividualResolutions(resolutionsById);
  };

  const goToReportPage = () => {
    const params = new URLSearchParams();
    if (haveValue(property_id)) params.set("property", property_id);
    sharedprofileid.forEach((id) => params.append("sharedprofileid", id));
    if (haveValue(rentVal)) params.set("rent", rentVal);
    if (haveValue(redirectUrl)) params.set("redirectUrl", redirectUrl);
    if (haveValue(selectionRaw)) params.set("selection", selectionRaw);
    if (awardSnapshotCompleted) params.set("awardSnapshotCompleted", "true");
    if (haveValue(historyEventId)) params.set("historyEventId", historyEventId);
    navigate(
      `/${workspaceId}/generatedReport/combinedReport?${params.toString()}`
    );
  };

  const handleProfileClick = (tenant) => {
    const baseUrl = `/${workspaceId}/profile-property?sharedprofileid=${tenant.sharedProfileId}`;
    const url =
      tenant.role === "tenant"
        ? `${baseUrl}&tenant_id=${tenant._id}`
        : `${baseUrl}&guarantor=${tenant._id}`;
    window.open(url, "_blank");
  };

  const formatMissingDoc = (val) => {
    const payslipMatch = val.match(/^payslip\s*-\s*(.+)$/i);
    if (payslipMatch) {
      return `${_t("Payslip")} - ${payslipMatch[1]}`;
    }
    return _t(val);
  };

  const resolvedWarnings = Object.entries(individualWarnings || {}).filter(
    ([, warning]) => warning?.isResolved
  );

  return showLocalLoading ? (
    <Loader />
  ) : (
    <>
      <div className="report-overview-comp" id="divToPrint">
        {/* Header */}
        <div className="report-overview__header">
          <div
            className="report-overview__back cursor-pointer no-print"
            onClick={handleGoBack}
          >
            <img src={Arrow} alt="" />
            <span>{_t("goBack")}</span>
          </div>

          <h1 className="report-overview__title">
            {[
              `${addressVal?.street ?? ""} ${
                addressVal?.street_number ?? ""
              }`.trim(),
              `${addressVal?.floor_number ? addressVal?.floor_number : ""} ${
                addressVal?.flat_number ? "-" + addressVal?.flat_number : ""
              }`,

              addressVal?.province,
            ]
              .filter(Boolean)
              .join(", ")}
          </h1>

          <div className="report-overview__subinfo">
            <span>
              {[addressVal?.postcode, addressVal?.muncipality]
                .filter(Boolean)
                .join(", ")}
            </span>
            <span className="report-overview__dot">•</span>
            <span>€{rentVal}/month</span>
          </div>

          <div className="report-overview__meta">
            <div className="report-overview__meta-item">
              <img src={"/images/calendar-icon.svg"} alt="" />
              <span>
                {todaysDate(addressVal?.award_snapshot_info?.created_at)}
              </span>
            </div>
            <div className="report-overview__meta-item">
              <img src={"/images/multiple-user.svg"} alt="" />{" "}
              <span>
                {" "}
                {sharedprofileid?.length} {_t("tenantsSelected")}
              </span>
            </div>
          </div>
        </div>

        {/* Approval Card */}
        <div
          style={STATUS_STYLES[status]}
          className="report-overview__approval-card"
        >
          {hasPendingChanges && (
            <div className="report-overview__resolve-actions no-print">
              <button
                type="button"
                className="report-overview__resolve-action"
                onClick={() => setShowResolveNoteModal(true)}
                disabled={resolvingDocs}
              >
                <span>
                  {resolvingDocs
                    ? `${_t("updating", "Updating")}...`
                    : resolveActionLabel}
                </span>
              </button>
            </div>
          )}

          <div className="report-overview__approval-status">
            <img src={STATUS_STYLES[status].img} />
            <span style={{ color: STATUS_STYLES[status].color }}>
              {STATUS_STYLES[status].text}
            </span>
          </div>

          <div className="report-overview__approval-metrics">
            <div>
              <strong>
                {_t("finalScore")}: {higherScoreProfile?.DEA_SCORE?.finalScore}
              </strong>{" "}
              / 10
            </div>

            <div>
              <strong>
                {_t("combinedEffortRate")}:{" "}
                {tenantData[0]?.vals?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE}%
              </strong>
            </div>

            <div className="report-overview__insurable">
              {tenantData?.some(
                (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
              ) ? (
                <img src={"/images/not-insurable.svg"} alt="" />
              ) : (
                <img src={"/images/shield.svg"} alt="" />
              )}
              <span
                style={{
                  color: tenantData?.some(
                    (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                  )
                    ? "#FF3C3C"
                    : "#82CF3F",
                }}
              >
                {_t(
                  tenantData?.some(
                    (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                  )
                    ? "notInsurable"
                    : "insurable"
                )}
              </span>
            </div>
          </div>

          <p className="report-overview__approval-text">
            {_t(
              status === "rejected"
                ? "oneOrMoreTenantsDoNotMeetTheRequiredRligibilityCriteriaForThisRental"
                : status === "review"
                  ? "someTenantsRequireAdditionalVerificationBeforeTheRentalCanBeApproved"
                  : "allSelectedTenantsMeetTheEligibilityCriteriaAndQualifyForInsuranceCoverage"
            )}
          </p>
          {(status === "rejected" ||
            status === "review" ||
            resolvedWarnings.length > 0) && (
            <div className="report-overview-declined-decision">
              <h1>{_t("whyThisDecision")}?</h1>
              <ul
                style={{
                  "--bullet-color": STATUS_STYLES[status].color,
                }}
              >
                {Object?.entries(individualError)?.map(([name, errors]) =>
                  errors.map(([_, value], index) => (
                    <li key={`${name}-${index}`}>
                      {name} {_t("has")} {_t(value?.label)}
                    </li>
                  ))
                )}
                {lowScoreCheck && (
                  <li key={`scoreissue`}>{_t("collectiveScoreIsLessThan4")}</li>
                )}
                {Object?.entries(individualWarnings)?.map(
                  ([tenantId, warning]) => {
                    const docsMissing = warning?.docsMissing;
                    const label = warning?.label;
                    const isResolved = !!warning?.isResolved;
                    const resolutionEntries = normalizeResolutions(
                      individualResolutions?.[tenantId]
                    );
                    const resolution = resolutionEntries?.[0];
                    const isChecked =
                      tenantId && selectedResolveTenantIds.includes(tenantId);
                    return (
                      <li
                        key={tenantId}
                        className="report-overview__warning-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          listStyleType: "none",
                          marginLeft: 0,
                        }}
                      >
                        <label className="custom-checkbox no-print">
                          <input
                            type="checkbox"
                            checked={!!isChecked}
                            disabled={!tenantId}
                            onChange={() => toggleResolveTenant(tenantId)}
                          />
                          <span className="custom-checkmark" />
                        </label>
                        <span
                          style={
                            isResolved
                              ? {
                                  textDecoration: "line-through",
                                  color: "#A3A3A3",
                                }
                              : undefined
                          }
                        >
                          {label} {_t("has")} {docsMissing?.length ?? 0}{" "}
                          {_t("missingDocuments")}
                        </span>
                        {isResolved && resolution?.landlordName && (
                          <span className="report-overview__resolved-by">
                            {_t("resolvedByLandlord", "Resolved by")}{" "}
                            <strong>{resolution?.landlordName}</strong>
                          </span>
                        )}
                        {isResolved && resolution?.note && (
                          <>
                            <span
                              className="report-overview__resolved-note no-print"
                              data-tooltip-id={`resolved-note-tooltip-${tenantId}`}
                            >
                              i
                            </span>
                            <ReactTooltip
                              id={`resolved-note-tooltip-${tenantId}`}
                              place="top"
                              noArrow
                              className="react-tooltip-styling report-overview-resolved-tooltip"
                              render={() => <p>{resolution?.note}</p>}
                            />
                          </>
                        )}
                      </li>
                    );
                  }
                )}
                {Object?.entries(missingDniNieWarning)?.map(([name]) => (
                  <li key={`${name}-dni`}>
                    {name}: {_t("missingDniOrNie")}
                  </li>
                ))}
              </ul>
              {/* {status === "rejected" && (
              <button>{_t("reviewFlaggedTenant")}</button>
            )} */}
            </div>
          )}
        </div>

        {/* Individual Tenant Scores */}
        <h2 className="report-overview__section-title">
          {_t("individualTenantScores")}
        </h2>
        {tenantData?.map((tenant) => (
          <div className="report-overview__tenant-card-comp">
            <div className="report-overview__tenant-card">
              <div className="report-overview__tenant-header">
                <div
                  style={{
                    backgroundColor: STATUS_STYLES["approved"].backgroundColor,
                    color: STATUS_STYLES["approved"].color,
                  }}
                  className="report-overview__avatar"
                >
                  {tenant?.label?.substring(0, 1)}
                </div>

                <div className="report-overview__tenant-info">
                  <h3
                    className={"d-flex align-items-center gap-1 cursor-pointer"}
                    onClick={() => handleProfileClick(tenant)}
                  >
                    <span>{tenant?.label ?? ""}</span>
                    <span
                      style={{ fontSize: "0.833vw", lineHeight: "normal" }}
                      className="neutral"
                    >
                      {_t(tenant?.role)}
                    </span>
                    <img
                      src="/images/external-link.svg"
                      alt=""
                      className="no-print"
                      style={{
                        width: "0.833vw",
                        height: "0.833vw",
                        marginLeft: "0.347vw",
                        verticalAlign: "middle",
                      }}
                    />
                  </h3>
                  <div className="report-overview__tenant-meta">
                    {haveValue(tenant?.paymentScores?.creditScore?.value) && (
                      <>
                        <span>
                          {_t("skor")}:{" "}
                          {tenant?.paymentScores?.creditScore?.value}
                        </span>
                        <span>•</span>
                      </>
                    )}
                    <span>
                      {_t("incomeVal")}: {tenant?.income}
                    </span>
                    <span>•</span>
                    <span
                      style={{
                        color:
                          tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true
                            ? "#E1BB20"
                            : "#82CF3F",
                        backgroundColor:
                          tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true
                            ? "#FFFBEC"
                            : "#F2FBE9",
                      }}
                      className="report-overview__badge"
                    >
                      {
                        <>
                          {tenant?.vals?.DEFAULT_HISTORY?.isHardFail ===
                          true ? (
                            ""
                          ) : (
                            <img src={"/images/shield.svg"} alt="" />
                          )}

                          {_t(
                            tenant?.vals?.DEFAULT_HISTORY?.isHardFail === true
                              ? "notInsurable"
                              : "insurable"
                          )}
                        </>
                      }
                    </span>
                  </div>
                </div>

                <div className="report-overview__score">
                  <div className="report-overview__score-value">
                    {tenant?.DEA_SCORE?.finalScore}
                  </div>
                  <div className="report-overview__score-label">
                    {_t("score")}
                  </div>
                </div>
              </div>

              {/* Tenant Details */}
              <div className="report-overview__tenant-details">
                <div className="report-overview__detail-row">
                  <span>{_t("debtRatio")}</span>
                  <span style={{ color: "#253837" }}>
                    {tenant?.DEA_SCORE?.EFFORT_RATE?.label}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.EFFORT_RATE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.EFFORT_RATE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.EFFORT_RATE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.EFFORT_RATE?.score}
                  </b>
                </div>

                <div className="report-overview__detail-row">
                  <span>{_t("seniority")}</span>
                  <span style={{ color: "#253837" }}>
                    {_t(tenant?.DEA_SCORE?.TENURE?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.TENURE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.TENURE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.TENURE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.TENURE?.score}
                  </b>
                </div>

                <div className="report-overview__detail-row">
                  <span>{_t("incomeType")}</span>
                  <span style={{ color: "#253837" }}>
                    {_t(tenant?.DEA_SCORE?.INCOME_TYPE?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.INCOME_TYPE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.INCOME_TYPE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.INCOME_TYPE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.INCOME_TYPE?.score}
                  </b>
                </div>

                <div className="report-overview__detail-row">
                  <span>{_t("employmentType")}</span>
                  <span style={{ color: "#253837" }}>
                    {_t(tenant?.DEA_SCORE?.CONTRACT_TYPE?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.CONTRACT_TYPE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.CONTRACT_TYPE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.CONTRACT_TYPE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.CONTRACT_TYPE?.score}
                  </b>
                </div>

                <div
                  style={{ borderBottom: "none" }}
                  className="report-overview__detail-row"
                >
                  <span>{_t("riskSector")}</span>
                  <span style={{ color: "#253837" }}>
                    {_t(tenant?.DEA_SCORE?.RISK_SECTORS?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.RISK_SECTORS?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.RISK_SECTORS?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.RISK_SECTORS?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.RISK_SECTORS?.score}
                  </b>
                </div>
              </div>

              {haveValue(individualError?.[tenant?.label]) && (
                <div className="report-overview-declined-review-info-comp">
                  <div className="report-overview-declined-review-info">
                    <img src="/images/notApproved.svg" alt="" />
                    <div>
                      <h1 style={{ color: STATUS_STYLES[status]?.color }}>
                        {_t("rejected")}
                      </h1>
                      <ul>
                        {individualError?.[tenant?.label]?.map((val) => (
                          <li>{_t(`${val[1]?.label}DescInOverview`)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {haveValue(individualWarnings?.[tenant?._id]) && (
                <div className="report-overview-declined-review-info-comp">
                  <div className="report-overview-declined-review-info">
                    <img src="/images/warning.svg" alt="" />
                    <div>
                      <h1 style={{ color: STATUS_STYLES["review"]?.color }}>
                        {_t("missingDocuments")}
                      </h1>
                      <ul>
                        {individualWarnings?.[tenant?._id]?.docsMissing?.map(
                          (val) => (
                            <li>{formatMissingDoc(val)}</li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        <div className="report-overview-footer no-print">
          <p>{_t("clickOnAnyTenantToViewDetailedScoringBreakdown")}</p>
          <div>
            <button
              className="btnNext"
              disabled={showSpinner}
              onClick={downloadPdfAndDocuments}
            >
              {!showSpinner && <img src={DownloadIcon} />}
              {showSpinner ? `${_t("downloading")}...` : _t("downloadReport")}
            </button>
            <button className="dark-button" onClick={goToReportPage}>
              {_t("viewFullDetails")}
            </button>
          </div>
        </div>
      </div>

      <Modal
        show={showResolveNoteModal}
        onHide={() => setShowResolveNoteModal(false)}
        centered
        className="newmodalui max-w-lg tailwind-base-modal wrapper-modal resolve-documents-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <span>{resolveActionLabel}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="resolve-documents-modal__summary">
            {pendingResolveIds.length > 0 && pendingUnresolveIds.length > 0
              ? _t("updatingDocumentsFor", "Updating documents for")
              : pendingUnresolveIds.length > 0
                ? _t("unresolvingDocumentsFor", "Unresolving documents for")
                : _t("resolvingDocumentsFor", "Resolving documents for")}{" "}
            <strong>{selectedResolveTenantIds.length}</strong>{" "}
            {_t("tenantS", "tenant(s)")}
          </p>
          {pendingResolveIds.length > 0 && (
            <div className="resolve-documents-modal__field">
              <textarea
                className="resolve-documents-modal__textarea"
                placeholder={_t("addNote", "Add a note (optional)")}
                aria-label={_t("addNote", "Add a note (optional)")}
                maxLength={500}
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
              />
              <span className="resolve-documents-modal__count">
                {resolveNote?.length}/500
              </span>
            </div>
          )}
          {pendingUnresolveIds.length > 0 && pendingResolveIds.length === 0 && (
            <p className="resolve-documents-modal__hint">
              {_t(
                "unresolveHint",
                "This will mark the documents as unresolved again."
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="resolve-documents-modal__button resolve-documents-modal__button--secondary"
            onClick={() => setShowResolveNoteModal(false)}
          >
            {_t("cancel")}
          </button>
          <button
            type="button"
            className="resolve-documents-modal__button resolve-documents-modal__button--primary"
            disabled={resolvingDocs}
            onClick={() => handleApplyResolutions(resolveNote)}
          >
            {resolvingDocs
              ? `${_t("updating", "Updating")}...`
              : resolveActionLabel}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReportOverview;
